import { useState, useEffect, useMemo } from 'react';
import { Download, Trash2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { API, authFetch, formatDate, exportCSV } from './helpers';

const NewsletterSubscribersTab = ({ user }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchSubscribers = async () => {
    const res = await authFetch(`${API}/newsletter`);
    if (res.ok) setSubscribers(await res.json());
  };

  useEffect(() => { fetchSubscribers(); }, []);

  const filtered = useMemo(() => {
    let items = subscribers;
    if (statusFilter !== 'all') items = items.filter(s => s.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(s => s.email?.toLowerCase().includes(q));
    }
    return items;
  }, [subscribers, search, statusFilter]);

  const deleteSubscriber = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    await authFetch(`${API}/newsletter/${id}`, { method: 'DELETE' });
    fetchSubscribers();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-brand-dark">Newsletter Subscribers</h2>
        <button onClick={() => exportCSV('newsletter')} className="flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-brand-dark hover:bg-gray-50" data-testid="export-newsletter-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="newsletter-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email..." className="pl-10" data-testid="newsletter-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-300 px-3 py-2 rounded text-sm" data-testid="newsletter-status-filter">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
      </div>

      <p className="text-xs text-gray-400 mb-4">{filtered.length} of {subscribers.length} subscribers</p>

      {filtered.length === 0 ? (
        <p className="text-gray-500">{subscribers.length === 0 ? 'No newsletter subscribers yet.' : 'No subscribers match your search.'}</p>
      ) : (
        <div className="overflow-x-auto" data-testid="subscribers-table">
          <table className="w-full bg-white">
            <thead>
              <tr className="border-b">
                <th className="text-left p-4 text-sm font-medium text-gray-600">Email</th>
                <th className="text-left p-4 text-sm font-medium text-gray-600">Source</th>
                <th className="text-left p-4 text-sm font-medium text-gray-600">Status</th>
                <th className="text-left p-4 text-sm font-medium text-gray-600">Subscribed</th>
                {user?.role === 'admin' && <th className="text-left p-4 text-sm font-medium text-gray-600">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((sub) => (
                <tr key={sub.id} className="border-b hover:bg-gray-50" data-testid={`subscriber-${sub.id}`}>
                  <td className="p-4 text-sm">{sub.email}</td>
                  <td className="p-4 text-sm capitalize">{sub.source.replace('_', ' ')}</td>
                  <td className="p-4"><span className={`px-2 py-0.5 text-xs font-medium ${sub.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{sub.status}</span></td>
                  <td className="p-4 text-sm text-gray-500">{formatDate(sub.created_at)}</td>
                  {user?.role === 'admin' && (
                    <td className="p-4"><button onClick={() => deleteSubscriber(sub.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NewsletterSubscribersTab;
